import type { KeyboardEvent, ReactNode } from "react";

import {
  IgAiIcon,
  IgButton,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { Box, Stack, useTheme } from "@mui/material";

import { ATLAS_SUGGESTIONS_ID } from "./atlasSuggestionIds";
import { AtlasSuggestions } from "./AtlasSuggestions";
import { useAtlasSuggestions } from "./useAtlasSuggestions";

interface AskAtlasProps {
  /** Heading beside the Atlas mark. */
  title: string;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  submitLabel: string;
  placeholder: string;
  /** Accessible name for the prompt input. */
  inputLabel: string;
  /**
   * Full-sentence starters offered under the input while it holds focus.
   * Omitted — or empty — and the composer carries no dropdown at all, which is
   * what lets surfaces without generated starters reuse this component.
   */
  suggestions?: readonly string[];
  suggestionsLabel?: string;
  attachmentControls?: ReactNode;
  attachmentPreview?: ReactNode;
}

/**
 * The Ask Atlas composer: the Atlas mark and heading over a single-line prompt
 * input that carries the attachment control and the submit button inside its
 * frame.
 */
export const AskAtlas = ({
  title,
  prompt,
  onPromptChange,
  onSubmit,
  canSubmit,
  submitLabel,
  placeholder,
  inputLabel,
  suggestions,
  suggestionsLabel,
  attachmentControls,
  attachmentPreview,
}: AskAtlasProps) => {
  const theme = useTheme();
  const suggestionList = useAtlasSuggestions({
    suggestions: suggestions ?? [],
    prompt,
    onPromptChange,
  });

  // Enter submits — unless the suggestion list claimed it to commit a
  // highlighted row (it marks the event handled) or the IME is mid-composition.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    suggestionList.handleKeyDown(event);
    if (event.defaultPrevented || event.key !== "Enter") {
      return;
    }
    if (event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }
    event.preventDefault();
    if (canSubmit) {
      onSubmit();
    }
  };

  return (
    <Stack spacing={3.5}>
      <Stack direction="row" alignItems="center" spacing={3}>
        <IgAiIcon
          size={32}
          glyphSize={18}
          surface="square-soft"
          sx={{ borderRadius: "6px" }}
        />
        <IgTypography component="h2" variant="h3" fontWeight={600}>
          {title}
        </IgTypography>
      </Stack>

      <Box sx={{ position: "relative" }}>
        <Stack
          // The same gradient focus frame the agent chat's composer uses, so
          // the two inputs stay one affordance rather than two look-alikes.
          className="ai-focus-border"
          aria-label="Rule composer"
          alignItems={{ xs: "stretch", sm: "center" }}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          role="group"
          spacing={2}
          sx={{
            // The composer is white on the page's off-white ground, so the
            // gradient frame is told what to fill its padding-box with.
            "--ai-focus-fill": theme.igBackgroundColors.baseLightHigh,
            bgcolor: theme.igBackgroundColors.baseLightHigh,
            border: `1px solid ${theme.igBorders.core.default}`,
            borderRadius: "6px",
            pl: 4,
            pr: 2,
            py: 2,
          }}
        >
          <IgTextField
            fullWidth
            label={null}
            placeholder={placeholder}
            value={prompt}
            variant="standard"
            slotProps={{
              htmlInput: {
                "aria-label": inputLabel,
                ...(suggestionList.isEnabled
                  ? {
                      role: "combobox",
                      "aria-autocomplete": "list",
                      "aria-expanded": suggestionList.isOpen,
                      "aria-controls": ATLAS_SUGGESTIONS_ID,
                      "aria-activedescendant": suggestionList.activeOptionId,
                    }
                  : {}),
              },
              input: { disableUnderline: true },
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              "& .MuiInputBase-input": {
                fontSize: { xs: 16, sm: 14 },
                lineHeight: "21px",
              },
            }}
            onChange={(event) => onPromptChange(event.target.value)}
            onFocus={suggestionList.handleFocus}
            onBlur={suggestionList.handleBlur}
            onKeyDown={handleKeyDown}
          />
          {attachmentPreview}
          <Stack direction="row" alignItems="center" spacing={2}>
            {attachmentControls}
            <IgButton
              variant="contained"
              disabled={!canSubmit}
              onClick={onSubmit}
              sx={{
                flexGrow: { xs: 1, sm: 0 },
                flexShrink: 0,
                height: 36,
                px: 4,
              }}
            >
              {submitLabel}
            </IgButton>
          </Stack>
        </Stack>
        {suggestionList.isOpen ? (
          <AtlasSuggestions
            suggestions={suggestionList.matches}
            activeIndex={suggestionList.activeIndex}
            label={suggestionsLabel}
            onHighlightSuggestion={suggestionList.highlight}
            onCommitSuggestion={suggestionList.commit}
          />
        ) : null}
      </Box>
    </Stack>
  );
};
